import { Injectable } from '@angular/core';
import * as Tiff from 'tiff';

@Injectable({
  providedIn: 'root',
})
export class ImageLoadingService {
  /**
   * Validates if the provided image type is supported
   */
  private isValidImageType(type: string): boolean {
    return /image\/(png|jpg|jpeg|bmp|gif|tiff|tif|webp)/.test(type);
  }

  /**
   * Loads an image from a File object
   */
  public loadImageFile(file: File): Promise<string> {
    return new Promise<string>((resolve: (value: string) => void, reject: (reason?: Error) => void) => {
      const fileReader: FileReader = new FileReader();
      fileReader.onload = (event: ProgressEvent<FileReader>): void => {
        if (this.isValidImageType(file.type)) {
          resolve(event.target?.result as string);
        } else {
          reject(new Error('Invalid image type'));
        }
      };
      fileReader.onerror = (): void => reject(new Error('Failed to read file'));
      fileReader.readAsDataURL(file);
    });
  }

  /**
   * Converts TIFF image to PNG format
   */
  private async convertTiffToPng(imageBase64: string): Promise<string> {
    return new Promise<string>((resolve: (value: string) => void, reject: (reason?: Error) => void) => {
      try {
        const buffer: Uint8Array = this.createBufferFromBase64(imageBase64);
        const ifds: unknown[] = Tiff.decode(buffer);

        if (!ifds || ifds.length === 0) {
          reject(new Error('Invalid TIFF data'));
          return;
        }

        const first: {
          width: number;
          height: number;
          data: Uint8Array;
          samplesPerPixel: number;
        } = ifds[0] as {
          width: number;
          height: number;
          data: Uint8Array;
          samplesPerPixel: number;
        };
        const canvas: HTMLCanvasElement = document.createElement('canvas');
        const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Canvas not supported'));
          return;
        }

        canvas.width = first.width;
        canvas.height = first.height;
        const rgbaData: Uint8ClampedArray = this.convertTiffToRgba(first);
        const imageData: ImageData = new ImageData(new Uint8ClampedArray(rgbaData), first.width, first.height);

        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (err: unknown) {
        const errorMessage: string = err instanceof Error ? err.message : 'Unknown error occurred';
        reject(new Error(`Error converting TIFF: ${errorMessage}`));
      }
    });
  }

  private createBufferFromBase64(imageBase64: string): Uint8Array {
    const byteString: string = atob(imageBase64.split(',')[1]);
    const buffer: Uint8Array = new Uint8Array(byteString.length);
    for (let i: number = 0; i < byteString.length; i++) {
      buffer[i] = byteString.charCodeAt(i);
    }
    return buffer;
  }

  private convertTiffToRgba(tiffData: {
    width: number;
    height: number;
    data: Uint8Array;
    samplesPerPixel: number;
  }): Uint8ClampedArray {
    const { width, height, data, samplesPerPixel } = tiffData;
    const rgbaData: Uint8ClampedArray = new Uint8ClampedArray(width * height * 4);
    const hasAlpha: boolean = samplesPerPixel === 4;

    for (let i: number = 0, j: number = 0; i < width * height; i++, j += 4) {
      rgbaData[j + 0] = data[i * samplesPerPixel + 0] & 0xff;
      rgbaData[j + 1] = data[i * samplesPerPixel + (samplesPerPixel > 1 ? 1 : 0)] & 0xff;
      rgbaData[j + 2] = data[i * samplesPerPixel + (samplesPerPixel > 2 ? 2 : 0)] & 0xff;
      rgbaData[j + 3] = hasAlpha ? data[i * 4 + 3] & 0xff : 255;
    }

    return rgbaData;
  }

  /**
   * Checks if the image is TIFF format and converts if necessary
   */
  public async processImageBase64(imageBase64: string): Promise<string> {
    if (imageBase64.startsWith('data:image/tiff;base64,') || imageBase64.startsWith('data:image/x-tiff;base64,')) {
      return await this.convertTiffToPng(imageBase64);
    }
    return imageBase64;
  }

  /**
   * Creates an HTMLImageElement from base64 data
   */
  public createImageFromBase64(imageBase64: string): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>(
      (resolve: (value: HTMLImageElement) => void, reject: (reason?: Error) => void) => {
        const img: HTMLImageElement = new Image();
        img.onload = (): void => resolve(img);
        img.onerror = (error: Event | string): void => {
          const errorMessage: string = error instanceof Error ? error.message : 'Unknown error occurred';
          reject(new Error(`Failed to load image: ${errorMessage}`));
        };
        img.src = imageBase64;
      }
    );
  }
}
